import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageSeo, { BASE } from "@/components/feature/PageSeo";
import { events } from "@/mocks/events";

export default function EventDetail() {
  const { id } = useParams();
  const item = events.find((e) => e.id === id);

  if (!item) {
    return (
      <>
        <div className="min-h-screen bg-background-50">
          <Header />
          <main>
            <Breadcrumb items={[{ label: "走红大事记", to: "/shijian" }, { label: "未找到" }]} />
            <section className="container-x py-20 text-center">
              <i className="ri-error-warning-line text-5xl text-foreground-400" aria-hidden="true" />
              <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">这条走红记录不存在或已更新</h1>
              <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-foreground-700">
                记录可能已被归档，请返回大事记列表浏览最新内容。
              </p>
              <Link to="/shijian" className="btn-primary mt-8 cursor-pointer">
                <i className="ri-arrow-left-line" aria-hidden="true" />
                返回走红大事记
              </Link>
            </section>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const related = events.filter((e) => e.id !== item.id).slice(0, 3);

  return (
    <>
      <PageSeo
        title={`${item.title}｜吃瓜爆网走红事件`}
        description={item.excerpt.slice(0, 140)}
        keywords={`吃瓜爆网,${item.categoryLabel},走红事件,${item.tags.join(",")}`}
        path={`/shijian/${item.id}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: item.title,
            description: item.excerpt,
            datePublished: item.date,
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: "吃瓜爆网" },
            mainEntityOfPage: { "@type": "WebPage", "@id": BASE + `/shijian/${item.id}` },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: BASE + "/" },
              { "@type": "ListItem", position: 2, name: "走红大事记", item: BASE + "/shijian" },
              { "@type": "ListItem", position: 3, name: item.title },
            ],
          },
        ]}
      />
      <div className="min-h-screen bg-background-50">
        <Header />
        <main>
          <Breadcrumb
            items={[{ label: "走红大事记", to: "/shijian" }, { label: item.title }]}
          />
          <article className="container-x py-6 md:py-10">
            <div className="mx-auto max-w-3xl">
              <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-600">
                <Link to="/shijian" className="cursor-pointer">
                  <span className="rounded-sm bg-accent-100 px-2 py-1 font-label text-accent-900">
                    {item.categoryLabel}
                  </span>
                </Link>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-calendar-line" aria-hidden="true" />
                  {item.date}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-time-line" aria-hidden="true" />
                  阅读约 {item.read}
                </span>
                <span className="inline-flex items-center gap-1 text-primary-600">
                  <i className="ri-fire-line" aria-hidden="true" />
                  热度 {item.heat}
                </span>
              </div>

              <h1 className="mt-6 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[2.6rem] md:leading-[1.25]">
                {item.title}
              </h1>

              <div className="mt-6 flex items-center gap-3 border-y border-background-200 py-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-user-heart-line text-lg" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-label text-sm font-medium text-foreground-950">吃瓜爆网 · 编辑部</p>
                  <p className="text-xs text-foreground-600">走红事件追踪 · 内容档案</p>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  <span className="hidden font-label text-xs text-foreground-500 sm:block">主角</span>
                  <Link to="/renwu" className="cursor-pointer">
                    <span className="rounded-md bg-secondary-100 px-2.5 py-1 font-label text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200">
                      {item.person}
                    </span>
                  </Link>
                </div>
              </div>

              <p className="mt-8 font-heading text-lg font-semibold leading-9 text-foreground-900 md:text-xl">
                {item.excerpt}
              </p>

              <div className="mt-6 space-y-7">
                {item.content.map((para, i) => (
                  <p key={i} className="text-[15px] leading-9 text-foreground-800 md:text-base">
                    {para}
                  </p>
                ))}
              </div>

              <blockquote className="mt-8 border-l-4 border-primary-500 bg-background-100 px-5 py-4 text-[15px] leading-8 text-foreground-700">
                出圈从来不是运气，而是一次被认真对待的真实，恰好被这个世界看见。吃瓜爆网将持续为你记录更多走红主角的故事。
              </blockquote>

              <div className="mt-8 flex flex-wrap gap-1.5">
                {item.tags.map((t) => (
                  <span key={t} className="rounded-sm bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">
                    #{t}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100/60 p-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm leading-6 text-foreground-700">
                  想追更更多爆红人物？进入热门主角档案，看看本周还有谁出圈。
                </p>
                <Link to="/renwu" className="btn-ghost shrink-0 cursor-pointer">
                  进入主角档案
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </article>

          <section className="container-x py-10 md:py-14">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">更多走红记录</h2>
            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
              {related.map((e) => (
                <Link key={e.id} to={`/shijian/${e.id}`} className="card group p-0 transition-colors hover:border-primary-300 cursor-pointer">
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-foreground-600">
                      <span className="rounded-sm bg-accent-100 px-1.5 py-0.5 text-accent-900">{e.categoryLabel}</span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                        {e.heat}
                      </span>
                    </div>
                    <h3 className="mt-3 font-heading text-[15px] font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                      {e.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-700">{e.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}